import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 4: Plasma Consciousness
 * Aurora-like particle system with flowing plasma colors
 */
export default function PlasmaAurora({ audioData, isActive, position = [0, 0, 0] }) {
  const pointsRef = useRef()
  const particleCount = 300

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Create particle positions and velocities
  const { positions, velocities, colors } = useMemo(() => {
    const positions = new Float32Array(particleCount * 3)
    const velocities = new Float32Array(particleCount * 3)
    const colors = new Float32Array(particleCount * 3)

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3

      // Initial positions - scattered in a volume
      positions[i3] = (Math.random() - 0.5) * 6
      positions[i3 + 1] = (Math.random() - 0.5) * 4 - 2
      positions[i3 + 2] = (Math.random() - 0.5) * 2

      // Initial velocities
      velocities[i3] = (Math.random() - 0.5) * 0.02
      velocities[i3 + 1] = Math.random() * 0.03 + 0.01 // Upward bias
      velocities[i3 + 2] = (Math.random() - 0.5) * 0.02

      // Aurora colors: cyan, purple, gold
      const colorChoice = Math.random()
      if (colorChoice < 0.33) {
        // Cyan
        colors[i3] = 0.0
        colors[i3 + 1] = 1.0
        colors[i3 + 2] = 1.0
      } else if (colorChoice < 0.66) {
        // Purple
        colors[i3] = 0.58
        colors[i3 + 1] = 0.0
        colors[i3 + 2] = 0.83
      } else {
        // Gold
        colors[i3] = 1.0
        colors[i3 + 1] = 0.84
        colors[i3 + 2] = 0.0
      }
    }

    return { positions, velocities, colors }
  }, [])

  // Create simplex-like noise function
  const noise = useMemo(() => {
    return (x, y, z, time) => {
      return Math.sin(x * 0.5 + time) * Math.cos(y * 0.5 + time * 0.7) * Math.sin(z * 0.5 + time * 0.3)
    }
  }, [])

  useFrame((state, delta) => {
    if (!isActive || !pointsRef.current) return

    const time = state.clock.elapsedTime
    const positionsAttr = pointsRef.current.geometry.attributes.position
    const colorsAttr = pointsRef.current.geometry.attributes.color

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3

      // Get current position
      let x = positionsAttr.array[i3]
      let y = positionsAttr.array[i3 + 1]
      let z = positionsAttr.array[i3 + 2]

      // Apply noise-based flow
      const noiseValue = noise(x, y, z, time)
      const flowX = Math.cos(time + noiseValue * Math.PI) * 0.02
      const flowZ = Math.sin(time + noiseValue * Math.PI) * 0.02

      // Update velocity with flow and audio influence
      velocities[i3] = flowX + bass * 0.01
      velocities[i3 + 1] = 0.02 + amplitude * 0.03 // Upward flow increases with amplitude
      velocities[i3 + 2] = flowZ + mids * 0.01

      // Update position
      x += velocities[i3]
      y += velocities[i3 + 1]
      z += velocities[i3 + 2]

      // Reset particles that flow too high
      if (y > 3) {
        y = -3
        x = (Math.random() - 0.5) * 6
        z = (Math.random() - 0.5) * 2
      }

      // Boundary wrapping for x and z
      if (x > 3) x = -3
      if (x < -3) x = 3
      if (z > 1) z = -1
      if (z < -1) z = 1

      // Update position
      positionsAttr.array[i3] = x
      positionsAttr.array[i3 + 1] = y
      positionsAttr.array[i3 + 2] = z

      // Color shifting based on audio spectrum
      const fftIndex = Math.floor((i / particleCount) * fft.length)
      const fftValue = fft[fftIndex] || 0

      if (fftValue > 0.5) {
        // Shift toward gold on high frequencies
        colorsAttr.array[i3] = Math.min(1, colorsAttr.array[i3] + delta * 0.6)
        colorsAttr.array[i3 + 1] = Math.min(1, colorsAttr.array[i3 + 1] + delta * 0.3)
      } else {
        // Fade back to original colors
        const originalIndex = i % 3
        if (originalIndex === 0) {
          // Cyan
          colorsAttr.array[i3] = Math.max(0, colorsAttr.array[i3] - delta * 0.3)
          colorsAttr.array[i3 + 1] = Math.min(1, colorsAttr.array[i3 + 1] + delta * 0.3)
          colorsAttr.array[i3 + 2] = Math.min(1, colorsAttr.array[i3 + 2] + delta * 0.3)
        } else if (originalIndex === 1) {
          // Purple
          colorsAttr.array[i3] = 0.58
          colorsAttr.array[i3 + 1] = Math.max(0, colorsAttr.array[i3 + 1] - delta * 0.3)
          colorsAttr.array[i3 + 2] = 0.83
        } else {
          // Gold
          colorsAttr.array[i3] = 1.0
          colorsAttr.array[i3 + 1] = 0.84
          colorsAttr.array[i3 + 2] = Math.max(0, colorsAttr.array[i3 + 2] - delta * 0.3)
        }
      }
    }

    positionsAttr.needsUpdate = true
    colorsAttr.needsUpdate = true
  })

  return (
    <group position={position}>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={particleCount}
            array={positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={particleCount}
            array={colors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.08}
          vertexColors
          transparent
          opacity={0.8}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </points>

      {/* Ambient lighting for particles */}
      <ambientLight intensity={0.5} />
    </group>
  )
}
